import { useForm } from "@tanstack/react-form"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { CalendarClock, CalendarRange } from "lucide-react"
import { useMemo, useState } from "react"

import { Button } from "@/components/button"
import { DateTimeField } from "@/components/custom/date-time-field"
import { CardHeading } from "@/components/shared/card-heading"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { guildWarApi } from "@/lib/guild-war-api"
import { guildWarKeys } from "@/lib/query-keys"
import {
  coerceDateTimeLocalValue,
  formatDateInput,
  parseDateInput,
} from "@/lib/utils/week-date-utils"

type CreateWeekFormValues = {
  weekId: string
  weekStartAt: string
  weekEndAt: string
}

const required =
  (message: string) =>
  ({ value }: { value: string }) =>
    value.trim().length > 0 ? undefined : message

const firstError = (errors: unknown[]) => {
  const value = errors[0]
  if (!value) return null
  if (value instanceof Error) return value.message
  if (typeof value === "string") return value
  if (
    typeof value === "object" &&
    value !== null &&
    "message" in value &&
    typeof value.message === "string"
  )
    return value.message
  return "Something went wrong"
}

type CreateWeekCardProps = {
  defaults: {
    weekId: string
    weekStartAt: string
    weekEndAt: string
  }
}

export function CreateWeekCard({ defaults }: CreateWeekCardProps) {
  const queryClient = useQueryClient()
  const [createWarning, setCreateWarning] = useState<string | null>(null)
  const [createErrorMsg, setCreateErrorMsg] = useState<string | null>(null)

  const createWeekMutation = useMutation({
    mutationFn: guildWarApi.createWeek,
    onSuccess: async (result) => {
      setCreateErrorMsg(null)
      setCreateWarning(
        result.warnings.length > 0 ? result.warnings.join(", ") : null,
      )
      await queryClient.invalidateQueries({ queryKey: guildWarKeys.weeks() })
    },
    onError: (error) => {
      setCreateErrorMsg(
        error instanceof Error ? error.message : "Failed to create week",
      )
    },
  })

  const createWeekForm = useForm({
    defaultValues: defaults satisfies CreateWeekFormValues,
    onSubmitInvalid: () => {
      setCreateErrorMsg("Please fix the highlighted form errors")
    },
    onSubmit: async ({ value }) => {
      setCreateErrorMsg(null)
      setCreateWarning(null)

      const weekStartAt = parseDateInput(value.weekStartAt)
      const weekEndAt = parseDateInput(value.weekEndAt)

      if (!weekStartAt || !weekEndAt) {
        setCreateErrorMsg("All times must be valid date-time values")
        return
      }

      if (weekStartAt >= weekEndAt) {
        setCreateErrorMsg("Week start must be before week end")
        return
      }

      try {
        await createWeekMutation.mutateAsync({
          weekId: value.weekId.trim(),
          weekStartAt,
          weekEndAt,
        })
      } catch {
        // Errors are surfaced through mutation onError into createErrorMsg.
      }
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardHeading icon={<CalendarRange className="size-5 text-primary" />}>
          Create New Week
        </CardHeading>
        <CardDescription>
          Set the new war window, confirm the ISO week identifier, and keep
          signup availability aligned with the <code>signup_open</code> state.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="metric-tile">
            <div className="metric-label">Suggested Week</div>
            <div className="mt-2 text-lg font-semibold text-foreground">
              {defaults.weekId}
            </div>
          </div>
          <div className="metric-tile">
            <div className="metric-label">Window Opens</div>
            <div className="mt-2 text-sm font-semibold text-foreground">
              {new Date(defaults.weekStartAt).toLocaleString()}
            </div>
          </div>
          <div className="metric-tile">
            <div className="metric-label">Window Closes</div>
            <div className="mt-2 text-sm font-semibold text-foreground">
              {new Date(defaults.weekEndAt).toLocaleString()}
            </div>
          </div>
        </div>

        <form
          className="grid gap-4 lg:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault()
            event.stopPropagation()
            void createWeekForm.handleSubmit()
          }}
        >
          <createWeekForm.Field
            name="weekId"
            validators={{
              onSubmit: required("Week ID is required"),
            }}
          >
            {(field) => {
              const error = firstError(field.state.meta.errors)
              const errorId = `${field.name}-error`
              return (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Week ID</Label>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={coerceDateTimeLocalValue(field.state.value)}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                  />
                  {error ? (
                    <p id={errorId} className="text-sm text-destructive">
                      {error}
                    </p>
                  ) : null}
                </div>
              )
            }}
          </createWeekForm.Field>

          <createWeekForm.Field
            name="weekStartAt"
            validators={{
              onSubmit: required("Week start time is required"),
            }}
          >
            {(field) => {
              const error = firstError(field.state.meta.errors)
              const errorId = `${field.name}-error`
              return (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Week Start (Local)</Label>
                  <DateTimeField
                    id={field.name}
                    value={coerceDateTimeLocalValue(field.state.value)}
                    onBlur={field.handleBlur}
                    onValueChange={(value) => field.handleChange(value ?? "")}
                    ariaInvalid={error ? true : undefined}
                    ariaDescribedBy={error ? errorId : undefined}
                    buttonClassName="w-full"
                  />
                  {error ? (
                    <p id={errorId} className="text-sm text-destructive">
                      {error}
                    </p>
                  ) : null}
                </div>
              )
            }}
          </createWeekForm.Field>

          <createWeekForm.Field
            name="weekEndAt"
            validators={{
              onSubmit: required("Week end time is required"),
            }}
          >
            {(field) => {
              const error = firstError(field.state.meta.errors)
              const errorId = `${field.name}-error`
              return (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Week End (Local)</Label>
                  <DateTimeField
                    id={field.name}
                    value={coerceDateTimeLocalValue(field.state.value)}
                    onBlur={field.handleBlur}
                    onValueChange={(value) => field.handleChange(value ?? "")}
                    ariaInvalid={error ? true : undefined}
                    ariaDescribedBy={error ? errorId : undefined}
                    buttonClassName="w-full"
                  />
                  {error ? (
                    <p id={errorId} className="text-sm text-destructive">
                      {error}
                    </p>
                  ) : null}
                </div>
              )
            }}
          </createWeekForm.Field>

          <div className="flex flex-wrap items-center gap-3 lg:col-span-3">
            <createWeekForm.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button
                  disabled={createWeekMutation.isPending || isSubmitting}
                  size="lg"
                  type="submit"
                >
                  <CalendarClock className="size-4" />
                  {createWeekMutation.isPending || isSubmitting
                    ? "Creating..."
                    : "Create Week"}
                </Button>
              )}
            </createWeekForm.Subscribe>
            {createErrorMsg ? (
              <p className="text-sm text-destructive">{createErrorMsg}</p>
            ) : null}
            {createWarning && !createErrorMsg ? (
              <p className="text-sm text-yellow-600">{createWarning}</p>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
